<template>
	<section
		:class="{
			'form-container dark:bg-gray-600': editing,
			'border border-gray-100': isDraggedOver && editing,
		}"
		data-test="session-title-description"
		@dragover.prevent="onDragOver"
		@dragend="isDraggedOver = false"
		@dragleave="isDraggedOver = false"
		@drop.prevent="onDropFiles"
	>
		<!-- EDITING MODE -->
		<template v-if="editing">
			<TextareaAutosize
				id="session-title"
				ref="title"
				v-model="title"
				placeholder="Add title"
				rows="1"
				data-test="session-title-input"
				class="text-lg font-semibold pt-4 mb-12"
				@keydown="onKeyDown"
				@input="onTitleChange"
				@blur="onTitleChange"
			/>

			<RichTextEditor
				id="session-description"
				ref="description"
				key="description-edit"
				v-model="description"
				placeholder="Add description"
				editable
				class="editing textarea description"
				:autofocus="false"
				data-test="session-description-rich-text-editor"
				enableTimestamps
				@close="cancel"
				@keydown="onKeyDown"
				@timestampClicked="onTimestampClicked"
				@timestampMouseOver="onTimestampMouseOver"
				@timestampMouseOut="onTimestampMouseOut"
			/>
			<div v-if="attachments.length" class="space-y-8 mt-12">
				<FileAttachment
					v-for="attachment in attachments"
					:key="attachment.id"
					:attachment="attachment"
					editing
					@remove="removeAttachment(attachment)"
				/>
			</div>
			<!-- form buttons -->
			<div class="flex items-center mt-16">
				<AttachFileButton v-if="!isSaving" :isDisabled="!$auth.isAuthenticated.value" @attach="onAttachFiles" />
				<!-- add marker button -->
				<BaseTooltip v-if="isVideo" placement="bottom" class="ml-8">
					<button class="text-gray-300 hover:text-gray-100" @click="addMarker">
						<BaseIcon name="mono/marker" class="w-18 h-18 mb-1" />
					</button>
					<template #text>
						<p class="flex items-center gap-4">
							Add marker at {{ timeString }} <span class="text-gray-400 text-xs">(Ctrl + M)</span>
						</p>
					</template>
				</BaseTooltip>

				<!-- only show save/cancel buttons in web -->
				<template v-if="!$store.isExtension">
					<button v-if="!isSaving" class="px-8 mr-12 h-28 link-gray ml-auto" @click="cancel">Cancel</button>

					<button
						class="button-blue text-strong-base"
						:class="{'ml-auto': isSaving}"
						:disabled="isSaving || hasFailedAttachmentFileUploads"
						@click="save"
					>
						<BaseIcon v-if="isSaving" name="mono/load" class="w-12 h-12 mr-4" />
						{{ isSaving ? 'Saving' : 'Save' }}
					</button>
				</template>
			</div>
		</template>

		<!-- DISPLAY MODE -->
		<template v-else>
			<div class="flex justify-between items-start">
				<h2
					id="session-title"
					ref="title"
					data-test="session-title"
					class="text-lg font-semibold pt-4 mb-12 min-w-0 break-words"
				>
					{{ title }}
				</h2>
				<BaseTooltip v-if="canUserEditSession && !editing" placement="bottom" text="Edit">
					<button
						type="button"
						class="button-icon text-gray-300"
						data-test="session-title-description-edit-button"
						@click="startEditing"
					>
						<BaseIcon name="mono/edit" />
					</button>
				</BaseTooltip>
			</div>
			<RichTextEditor
				v-if="description"
				id="session-description"
				key="description-display"
				ref="description-display"
				:modelValue="description"
				:editable="false"
				:autofocus="false"
				enableTimestamps
				class="description"
				data-test="session-description-rich-text-editor"
				@timestampClicked="onTimestampClicked"
				@timestampMouseOver="onTimestampMouseOver"
				@timestampMouseOut="onTimestampMouseOut"
			/>
			<div v-if="attachments.length" class="space-y-8 mt-12">
				<FileAttachment v-for="attachment in attachments" :key="attachment.id" :attachment="attachment" />
			</div>
		</template>
	</section>
</template>

<script>
import {defineAsyncComponent} from 'vue'
import {storeToRefs} from 'pinia'

import AttachFileButton from 'shared/components/AttachFileButton.vue'
import FileAttachment from 'shared/components/FileAttachment.vue'
import manageAttachments from 'shared/mixins/manageAttachments'
import TextareaAutosize from 'shared/components/TextareaAutosize.vue'
import {humanDurationShort} from 'shared/utils/humanDuration'
import {useSessionStore} from 'shared/stores/session'

export default {
	components: {
		AttachFileButton,
		FileAttachment,
		TextareaAutosize,
		RichTextEditor: defineAsyncComponent(() => import('shared/components/RichTextEditor.vue')),
	},
	mixins: [manageAttachments],
	setup() {
		const sessionStore = useSessionStore()
		const {session, canUserEditSession, currentTimeSeconds} = storeToRefs(sessionStore)
		return {session, canUserEditSession, currentTimeSeconds, sessionStore}
	},
	data() {
		return {
			title: '',
			description: '',
			editing: false,
			isSaving: false,
			isDraggedOver: false,
		}
	},
	computed: {
		hasUnsavedChanges() {
			return (
				this.title?.trim() !== this.session.title?.trim() ||
				this.description?.trim() !== this.session.description?.trim() ||
				this.newAttachments.length ||
				this.removedAttachments.length
			)
		},
		timeString() {
			return humanDurationShort(this.currentTimeSeconds * 1000)
		},
		isVideo() {
			if (this.session?.video != undefined) return Boolean(this.session.video)
			if (this.$store.isExtension) {
				return !this.session?.isInstantReplay && this.$route.query.mediaType !== 'Screenshot'
			}
			return false
		},
	},
	watch: {
		description: {
			handler(description) {
				if (!this.$store.isExtension) return
				if (sessionStorage) sessionStorage.sessionDescription = description
			},
			immediate: true,
		},
		// for when title and description is changed through subscription
		'session.title'() {
			this.title = this.session.title
		},
		'session.description'() {
			this.description = this.convertPlainTextDescriptionToHTML(this.session.description)
		},
		'session.attachments': {
			immediate: true,
			deep: true,
			handler(attachments) {
				if (!Array.isArray(attachments)) return
				this.currentAttachments = attachments
			},
		},
		hasUnsavedChanges(hasUnsavedChanges) {
			if (!sessionStorage) return
			if (hasUnsavedChanges) {
				sessionStorage.hasUnsavedChanges = true
			} else {
				delete sessionStorage.hasUnsavedChanges
			}
		},
	},
	mounted() {
		this.title = this.session.title || ''
		this.description = this.convertPlainTextDescriptionToHTML(this.session.description)

		// enable editing by default in extension's LocalSession
		if (this.$store.isExtension) {
			this.startEditing()
		}
	},
	methods: {
		onTimestampClicked(seconds) {
			this.sessionStore.seek(seconds)
		},
		onTimestampMouseOver(seconds) {
			window.dispatchEvent(new CustomEvent('timestamp-mouse-over', {detail: seconds}))
		},
		onTimestampMouseOut(seconds) {
			window.dispatchEvent(new CustomEvent('timestamp-mouse-out', {detail: seconds}))
		},
		onAttachFiles(event) {
			this.uploadAttachmentFiles(event.target.files)
		},
		onDragOver() {
			if (!this.$auth.isAuthenticated.value || this.isSaving) return
			this.isDraggedOver = true
		},
		onDropFiles(event) {
			if (!this.editing || !this.$auth.isAuthenticated.value || this.isSaving) return
			this.uploadAttachmentFiles(event.dataTransfer.files)
			this.isDraggedOver = false
		},
		focus(field) {
			if (field === 'title') {
				this.$refs.title?.$refs?.textarea?.focus()
				return
			}
			//focus description field
			this.$refs.description?.editor.commands.focus('start')
		},
		startEditing() {
			if (!this.canUserEditSession) return
			this.editing = true
			this.$nextTick(() => {
				this.focus('title')
			})
		},
		onTitleChange() {
			if (!this.$store.isExtension) return
			if (sessionStorage) sessionStorage.sessionTitle = this.title
		},
		async onKeyDown(event) {
			const key = event.key
			const field = event.target.id === 'session-title' ? 'title' : 'description'

			if (key === 'Escape' && field === 'title') {
				this.cancel()
				return
			}

			const cursorPosition =
				field === 'title' ? event.target.selectionStart : this.$refs.description?.editor.state.selection.anchor
			const titleLength = this.title.length

			switch (key) {
				case 'Enter':
					if (event.ctrlKey || event.metaKey) {
						this.save()
						break
					}
					if (field === 'title') {
						event.preventDefault()
						this.focus('description')
					}
					break
				case 'ArrowDown':
					if (field === 'description') break
					if (cursorPosition === titleLength || cursorPosition === 0) {
						this.focus('description')
					}
					break
				case 'ArrowRight':
					if (field === 'description') break
					if (cursorPosition === titleLength) {
						this.focus('description')
					}
					break
				case 'ArrowUp':
				case 'ArrowLeft':
					if (field === 'title') break
					if (cursorPosition === 1) {
						event.preventDefault()
						this.focus('title')
					}
					break
				case 'm':
					if (field === 'description' && (event.ctrlKey || event.metaKey)) {
						event.preventDefault()
						this.addMarker()
					}
					break
			}
		},
		addMarker() {
			// because the marker is rounded off to the nearest second, we need to adjust the video's position and its progress bar
			// so that the marker's position on the bar aligns well with the bar's position
			this.sessionStore.seek(parseInt(this.currentTimeSeconds))
			const editor = this.$refs.description.editor
			const position = editor.state.selection.anchor
			const content = `${this.timeString} `
			editor.commands.insertContentAt(position, content, {updateSelection: true})
			editor.commands.focus()
		},
		cancel() {
			if (this.$store.isExtension) return // in the extension, it's not possible to cancel editing
			if (this.isSaving) return // don't allow to cancel while saving
			this.title = this.session.title
			this.description = this.convertPlainTextDescriptionToHTML(this.session.description)
			this.discardAttachmentChanges()
			this.editing = false
		},
		async save() {
			if (!this.canUserEditSession) return
			if (!this.editing) return
			if (this.$store.isExtension) return // in the extension, it's not possible to save editing

			this.isSaving = true
			const title = this.title.trim()
			const description = this.description.trim()
			if (!this.hasUnsavedChanges) {
				this.isSaving = false
				this.editing = false
				return
			}
			try {
				const updateSessionTitleAndDescription = (
					await import('shared/apollo/updateSessionTitleAndDescription')
				).default
				await updateSessionTitleAndDescription({
					collectionId: this.session.collectionId,
					sessionId: this.session.id,
					userId: this.$store.user.id,
					title,
					description,
				})
				await this.saveAttachmentChanges({sessionId: this.session.id})
				this.editing = false
			} finally {
				this.isSaving = false
			}
		},
		convertPlainTextDescriptionToHTML(description) {
			if (!description) return ''
			// before rich-text-editing, plain-text descriptions included \n characters for new lines
			// as tiptap ignores them when setting its content, we convert them to HTML before setting
			// the content on the editor... any saves or new comments will always contain the full HTML
			return description.includes('\n')
				? description
						.split('\n')
						.map(line => `<p>${line}</p>`)
						.join('')
				: description
		},
	},
}
</script>

<style scoped>
.form-container {
	padding: 8px 12px 12px 12px;
	border-radius: 8px;
	border: 1px solid var(--gray-500);
}

.textarea {
	display: block;
	width: 100%;
	background-color: transparent;
	resize: none;
}

.description {
	font-size: 14px;
	color: var(--gray-300);
	width: 100%;
}

.editing {
	color: var(--gray-100);
}
</style>
